import {
  type ElementEventBasePayload,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import React, {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { DragData, DropData, DropPosition } from './types';
import { extractDragPayload } from './useDraggableNode';
import {
  extractDropPayload,
  resolveDropPositionFromTarget,
} from './useDropTargetNode';
import { type PerformMove, useMoveNode } from './useMoveNode';

interface DragDropProviderProps {
  children: React.ReactNode;
}

interface ActiveContext {
  data: DragData;
  label: string;
}

const ActiveDragContext = React.createContext<ActiveContext | null>(null);
export const useActiveDrag = () => React.useContext(ActiveDragContext);

const resolvePosition = (
  drop: DropData,
  drag: DragData,
  targetData: Record<string | symbol, unknown>,
): DropPosition | null => {
  if (drop.kind === 'root-drop') return 'inside';
  if (drop.kind === 'folder-drop' && drag.kind !== 'folder') return 'inside';
  if (drop.kind === 'item-drop' && drag.kind === 'folder') return null;
  return resolveDropPositionFromTarget(targetData, drop.kind);
};

interface DndMonitorProps {
  setActive: Dispatch<SetStateAction<ActiveContext | null>>;
  performMoveRef: MutableRefObject<PerformMove>;
}

const DndMonitor: React.FC<DndMonitorProps> = React.memo(
  ({ setActive, performMoveRef }) => {
    // biome-ignore lint/correctness/useExhaustiveDependencies: monitor is intentionally mount-once; reads via refs.
    useEffect(() => {
      return monitorForElements({
        onDragStart: ({ source }) => {
          const payload = extractDragPayload(source.data);
          if (payload)
            setActive({ data: payload.drag, label: payload.label ?? '' });
        },
        onDrop: (payload: ElementEventBasePayload) => {
          setActive(null);
          const dragPayload = extractDragPayload(payload.source.data);
          const target = payload.location.current.dropTargets[0];
          if (!dragPayload || !target) return;
          const dropPayload = extractDropPayload(target.data);
          if (!dropPayload) return;
          const position = resolvePosition(
            dropPayload.drop,
            dragPayload.drag,
            target.data,
          );
          if (!position) return;
          void performMoveRef.current({
            active: dragPayload.drag,
            over: dropPayload.drop,
            position,
          });
        },
      });
    }, []);
    return null;
  },
);
DndMonitor.displayName = 'DndMonitor';

interface MoveNodeHostProps {
  setActive: Dispatch<SetStateAction<ActiveContext | null>>;
}

const MoveNodeHost: React.FC<MoveNodeHostProps> = React.memo(
  ({ setActive }) => {
    const { performMove } = useMoveNode();
    const performMoveRef = useRef(performMove);
    useLayoutEffect(() => {
      performMoveRef.current = performMove;
    }, [performMove]);
    return <DndMonitor setActive={setActive} performMoveRef={performMoveRef} />;
  },
);
MoveNodeHost.displayName = 'MoveNodeHost';

export const DragDropProvider: React.FC<DragDropProviderProps> = ({
  children,
}) => {
  const [active, setActive] = useState<ActiveContext | null>(null);
  return (
    <ActiveDragContext.Provider value={active}>
      <MoveNodeHost setActive={setActive} />
      {children}
    </ActiveDragContext.Provider>
  );
};
